import { ENDPOINT } from '~/constants';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type BulkRetryParsingCasesPayload,
  type BulkRetryParsingCasesResponse,
} from './types';
import {
  zodBulkRetryParsingCasesPayloadSchema,
  zodBulkRetryParsingCasesResponseSchema,
} from './validations';

const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  403: () =>
    i18n.t(($) => $.bulkRetryParsingCases.error403, {
      ns: 'data',
    }),
  422: () =>
    i18n.t(($) => $.bulkRetryParsingCases.error422, {
      ns: 'data',
    }),
};

/**
 * Bulk retry parsing cases from start (full pipeline per case).
 *
 * @param payload - Case IDs to retry (1–100 items; duplicates collapsed by backend).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases
 */
export const bulkRetryParsingCases = async (
  payload: BulkRetryParsingCasesPayload,
): Promise<BulkRetryParsingCasesResponse> => {
  try {
    return await vestigasApi
      .post(ENDPOINT.PARSING_CASE.BULK_RETRY(), {
        json: payload,
        validate: {
          body: zodBulkRetryParsingCasesPayloadSchema,
          response: zodBulkRetryParsingCasesResponseSchema,
        },
      })
      .json<BulkRetryParsingCasesResponse>();
  } catch (error) {
    log.error({
      message: 'Error bulk retrying parsing cases',
      error,
    });

    const errorMessage = resolveMutationErrorMessage(
      error,
      ERROR_MESSAGE_BY_STATUS,
      () =>
        i18n.t(($) => $.bulkRetryParsingCases.error.generic, {
          ns: 'data',
        }),
    );
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
